import { memo, useMemo, useState, useCallback } from 'react';
import {
    Box, Flex, Grid, Heading, Text, Image, Input, InputGroup,
    InputLeftElement, IconButton, Button, Skeleton, SkeletonCircle,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import {
    useVendors,
    normalizeVendor,
    buildVendorUrl,
} from 'hooks/useVendors';

/* ─────────────────────────────────────────────────────────────
   AllVendors page — /dashboard/vendors
   Visual consistency target: matches AllBrands exactly.
     - Header with back icon + title + count
     - Search input (server-side via useVendors search param)
     - Letter-grouped alphabetical sections
     - Grid of vendor cards (avatar + name only)
     - Empty / loading / no-results states
   ───────────────────────────────────────────────────────────── */

const NAVY        = '#1B3B5F';
const NAVY_DEEP   = '#0F2940';
const CYAN        = '#DCF8F9';
const INK         = '#1A2740';
const MUTED       = '#5A6B7E';
const FAINT       = '#8896AE';
const SURFACE     = '#FFFFFF';
const SURFACE_ALT = '#F7F9FB';
const BORDER      = '#E8EDF5';

const SHADOW_SM = '0 1px 3px rgba(27,59,95,0.06)';
const FONT      = "'Bricolage Grotesque', sans-serif";

/* ─────────────────────────────────────────────────────────────
   ICONS
   ───────────────────────────────────────────────────────────── */
const SearchIcon = (props) => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" {...props}>
        <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
        <path d="M21 21l-4.35-4.35" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
);
const CloseIcon = (props) => (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" {...props}>
        <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
);
const StoreIcon = (props) => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" {...props}>
        <path d="M3 9l1.5-5h15L21 9 M3 9v11h18V9 M3 9h18 M8 14h8" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
    </svg>
);
const ChevronLeftIcon = (props) => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" {...props}>
        <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const toInitial = (name) => (name?.[0] ?? '?').toUpperCase();

/* ─────────────────────────────────────────────────────────────
   VENDOR AVATAR
   ───────────────────────────────────────────────────────────── */
const VendorAvatar = memo(function VendorAvatar({ vendor, size, fontSize }) {
    const initial = toInitial(vendor?.name);
    return (
        <Flex
            w={size}
            h={size}
            borderRadius="full"
            bg={vendor?.image ? SURFACE : CYAN}
            color={NAVY}
            align="center"
            justify="center"
            fontWeight="800"
            fontSize={fontSize}
            fontFamily={FONT}
            overflow="hidden"
            border="1px solid"
            borderColor={BORDER}
            flexShrink={0}
        >
            {vendor?.image ? (
                <Image
                    src={vendor.image}
                    alt={vendor.name}
                    w="full"
                    h="full"
                    objectFit="cover"
                    fallback={
                        <Flex w="full" h="full" align="center" justify="center" bg={CYAN}>
                            {initial}
                        </Flex>
                    }
                />
            ) : (
                initial
            )}
        </Flex>
    );
});

/* ─────────────────────────────────────────────────────────────
   VENDOR CARD — matches AllBrands BrandCard exactly
   ───────────────────────────────────────────────────────────── */
const VendorCard = memo(function VendorCard({ vendor, onClick }) {
    return (
        <Flex
            direction="column"
            align="center"
            gap="8px"
            p={{ base: '10px 6px', md: '14px 10px' }}
            borderRadius="10px"
            cursor="pointer"
            onClick={onClick}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onClick?.();
                }
            }}
            border="1px solid"
            borderColor={BORDER}
            bg={SURFACE}
            transition="all 0.15s ease"
            boxShadow={SHADOW_SM}
            minW="0"
            w="full"
            _hover={{
                borderColor: NAVY,
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 16px rgba(27,59,95,0.10)',
            }}
            role="button"
            tabIndex={0}
            aria-label={`View ${vendor?.name} products`}
        >
            <VendorAvatar
                vendor={vendor}
                size={{ base: '44px', md: '56px' }}
                fontSize={{ base: '15px', md: '18px' }}
            />
            <Box textAlign="center" minW="0" w="full">
                <Text
                    fontSize={{ base: '10px', md: '11.5px' }}
                    fontWeight="700"
                    color={INK}
                    noOfLines={2}
                    lineHeight="1.25"
                    minH={{ base: '25px', md: '29px' }}
                    fontFamily={FONT}
                >
                    {vendor?.name}
                </Text>
            </Box>
        </Flex>
    );
});

const VendorCardSkeleton = memo(function VendorCardSkeleton() {
    return (
        <Box
            p={{ base: '10px 6px', md: '14px 10px' }}
            borderRadius="10px"
            border="1px solid"
            borderColor={BORDER}
            bg={SURFACE}
            minW="0"
            w="full"
        >
            <Flex direction="column" align="center" gap="8px">
                <SkeletonCircle size={{ base: '44px', md: '56px' }} startColor={SURFACE_ALT} endColor={BORDER} />
                <Skeleton h="2.5" w="80%" borderRadius="3px" startColor={SURFACE_ALT} endColor={BORDER} />
            </Flex>
        </Box>
    );
});

/* ─────────────────────────────────────────────────────────────
   LETTER DIVIDER
   ───────────────────────────────────────────────────────────── */
const LetterDivider = memo(function LetterDivider({ letter, count }) {
    return (
        <Flex
            align="center"
            gap="10px"
            mb="10px"
            w="full"
            minW="0"
        >
            <Flex
                w="26px"
                h="26px"
                borderRadius="7px"
                bg={NAVY}
                color="white"
                align="center"
                justify="center"
                fontWeight="800"
                fontSize="12px"
                fontFamily={FONT}
                flexShrink={0}
            >
                {letter}
            </Flex>
            <Box flex="1" minW="0" h="1px" bg={BORDER} />
            <Text fontSize="10px" color={FAINT} fontWeight="700" flexShrink={0}>
                {count}
            </Text>
        </Flex>
    );
});

/* ─────────────────────────────────────────────────────────────
   EMPTY STATE
   ───────────────────────────────────────────────────────────── */
const EmptyState = ({ hasSearch, onClearSearch }) => (
    <Flex
        direction="column"
        align="center"
        justify="center"
        py={{ base: '40px', md: '60px' }}
        px="20px"
        textAlign="center"
        gap="12px"
        bg={SURFACE}
        border="1px solid"
        borderColor={BORDER}
        borderRadius="12px"
    >
        <Flex w="56px" h="56px" borderRadius="full" bg={SURFACE_ALT} color={FAINT} align="center" justify="center">
            <StoreIcon />
        </Flex>
        <Box>
            <Heading as="h3" fontSize="14px" fontWeight="800" color={INK} fontFamily={FONT} mb="4px">
                {hasSearch ? 'No vendors match your search' : 'No vendors available yet'}
            </Heading>
            <Text fontSize="11.5px" color={MUTED}>
                {hasSearch ? 'Try a different search term.' : 'Check back soon — new sellers join regularly.'}
            </Text>
        </Box>
        {hasSearch && (
            <Button
                onClick={onClearSearch}
                size="sm"
                bg={NAVY}
                color="white"
                _hover={{ bg: NAVY_DEEP }}
                fontSize="11.5px"
                fontWeight="700"
                borderRadius="8px"
                h="34px"
                px="12px"
            >
                Clear search
            </Button>
        )}
    </Flex>
);

/* ─────────────────────────────────────────────────────────────
   MAIN
   ───────────────────────────────────────────────────────────── */
const AllVendors = () => {
    const navigate = useNavigate();

    const [searchValue, setSearchValue] = useState('');

    /* Server-side search via useVendors hook — built-in debounce */
    const { vendors: rawVendors, isLoading, totalCount } = useVendors({
        per_page: 500,
        search: searchValue,
        debounce: 300,
    });

    const allVendors = useMemo(
        () => rawVendors.map(normalizeVendor),
        [rawVendors]
    );

    /* Group alphabetically — letters first, non-letters under '#' */
    const groupedVendors = useMemo(() => {
        const groups = {};
        allVendors.forEach((v) => {
            if (!v?.name) return;
            const letter = (v.name?.[0] ?? '#').toUpperCase();
            const key = /[A-Z]/.test(letter) ? letter : '#';
            if (!groups[key]) groups[key] = [];
            groups[key].push(v);
        });
        return Object.keys(groups)
            .sort((a, b) => {
                if (a === '#') return 1;
                if (b === '#') return -1;
                return a.localeCompare(b);
            })
            .map((letter) => ({ letter, vendors: groups[letter] }));
    }, [allVendors]);

    const goToVendor = useCallback(
        (vendor) => {
            if (!vendor) return;
            navigate(buildVendorUrl(vendor.raw ?? vendor));
        },
        [navigate]
    );

    const hasSearch = searchValue.trim().length > 0;
    const noResults = !isLoading && allVendors.length === 0;

    return (
        <Box
            bg={SURFACE_ALT}
            minH="100vh"
            fontFamily={FONT}
            px={{ base: '12px', md: '24px' }}
            py={{ base: '12px', md: '20px' }}
            w="full"
            maxW="100vw"
            overflowX="hidden"
        >
            <Box maxW="1280px" mx="auto" w="full" minW="0">
                {/* HEADER */}
                <Flex align="center" gap="8px" mb="14px">
                    <IconButton
                        aria-label="Back to dashboard"
                        icon={<ChevronLeftIcon />}
                        onClick={() => navigate('/dashboard')}
                        variant="ghost"
                        size="sm"
                        color={MUTED}
                        _hover={{ bg: SURFACE, color: NAVY }}
                        h="32px"
                        minW="32px"
                        flexShrink={0}
                    />
                    <Box flex="1" minW="0">
                        <Heading
                            as="h1"
                            fontSize={{ base: '18px', md: '24px' }}
                            fontWeight="800"
                            color={INK}
                            fontFamily={FONT}
                            letterSpacing="-0.02em"
                            lineHeight="1.2"
                            noOfLines={1}
                        >
                            All Vendors
                        </Heading>
                        <Text fontSize="11px" color={MUTED} mt="2px" noOfLines={1}>
                            {isLoading
                                ? 'Loading…'
                                : hasSearch
                                ? `${allVendors.length} match${allVendors.length === 1 ? '' : 'es'}`
                                : `${totalCount || allVendors.length} vendor${
                                      (totalCount || allVendors.length) === 1 ? '' : 's'
                                  } available`}
                        </Text>
                    </Box>
                </Flex>

                {/* SEARCH */}
                <Box mb="20px" w="full" minW="0">
                    <InputGroup>
                        <InputLeftElement pointerEvents="none" h="42px">
                            <Box color={FAINT}><SearchIcon /></Box>
                        </InputLeftElement>
                        <Input
                            type="search"
                            value={searchValue}
                            onChange={(e) => setSearchValue(e.target.value)}
                            placeholder="Search vendors…"
                            h="42px"
                            pl="40px"
                            pr={searchValue ? '40px' : '14px'}
                            fontSize="13px"
                            bg={SURFACE}
                            border="1px solid"
                            borderColor={BORDER}
                            borderRadius="10px"
                            fontFamily={FONT}
                            _placeholder={{ color: FAINT }}
                            _hover={{ borderColor: '#CBD5E0' }}
                            _focus={{
                                borderColor: NAVY,
                                boxShadow: `0 0 0 3px ${CYAN}`,
                                bg: SURFACE,
                            }}
                        />
                        {searchValue && (
                            <Box
                                position="absolute"
                                right="6px"
                                top="50%"
                                transform="translateY(-50%)"
                                zIndex={2}
                            >
                                <IconButton
                                    aria-label="Clear search"
                                    icon={<CloseIcon />}
                                    onClick={() => setSearchValue('')}
                                    variant="ghost"
                                    size="xs"
                                    h="28px"
                                    minW="28px"
                                    color={FAINT}
                                    _hover={{ color: INK, bg: 'transparent' }}
                                />
                            </Box>
                        )}
                    </InputGroup>
                </Box>

                {/* BODY */}
                {isLoading ? (
                    <Grid
                        templateColumns={{
                            base: 'repeat(3, 1fr)',
                            sm: 'repeat(4, 1fr)',
                            md: 'repeat(5, 1fr)',
                            lg: 'repeat(6, 1fr)',
                            xl: 'repeat(8, 1fr)',
                        }}
                        gap={{ base: '8px', md: '10px' }}
                        w="full"
                    >
                        {Array.from({ length: 18 }).map((_, i) => (
                            <VendorCardSkeleton key={i} />
                        ))}
                    </Grid>
                ) : noResults ? (
                    <EmptyState
                        hasSearch={hasSearch}
                        onClearSearch={() => setSearchValue('')}
                    />
                ) : (
                    <Flex direction="column" gap={{ base: '16px', md: '24px' }}>
                        {groupedVendors.map(({ letter, vendors }) => (
                            <Box key={letter} w="full" minW="0">
                                <LetterDivider letter={letter} count={vendors.length} />
                                <Grid
                                    templateColumns={{
                                        base: 'repeat(3, 1fr)',
                                        sm: 'repeat(4, 1fr)',
                                        md: 'repeat(5, 1fr)',
                                        lg: 'repeat(6, 1fr)',
                                        xl: 'repeat(8, 1fr)',
                                    }}
                                    gap={{ base: '8px', md: '10px' }}
                                    w="full"
                                >
                                    {vendors.map((v) => (
                                        <VendorCard
                                            key={v.id ?? v.name}
                                            vendor={v}
                                            onClick={() => goToVendor(v)}
                                        />
                                    ))}
                                </Grid>
                            </Box>
                        ))}
                    </Flex>
                )}
            </Box>
        </Box>
    );
};

export default AllVendors;